import httpClient from 'services/httpClient';

/**
 * Chat / direct-messaging API (`/chat`).
 *
 * The durable side of chat: conversation management and message history. Live
 * delivery/typing goes over the WebSocket (see services/socket.js); `send` here
 * is the REST fallback used when the socket isn't open. httpClient returns the
 * `{ success, message, data, meta }` envelope and injects the auth token.
 *
 * Response shapes:
 *   listConversations() -> { data: [ { id, title, participants, last_message, unread_count } ], meta }
 *   openDirect()        -> { data: { id } }
 *   listMessages()      -> { data: [ { id, conversation_id, sender_id, sender_name, body, created_at } ] }
 *   send()              -> { data: { …message } }
 */
export const chatService = {
  // GET /chat/conversations
  listConversations: () => httpClient.get('/chat/conversations'),
  // GET /chat/contacts — users the caller may start a conversation with
  listContacts: () => httpClient.get('/chat/contacts'),
  // POST /chat/conversations/direct — open (or reuse) a 1:1 with another user
  openDirect: (userId) => httpClient.post('/chat/conversations/direct', { user_id: userId }),
  // POST /chat/conversations/group — create a one-to-many group
  createGroup: (title, userIds) =>
    httpClient.post('/chat/conversations/group', { title, user_ids: userIds }),
  // PATCH /chat/conversations/:id — rename a group (owner only)
  rename: (id, title) => httpClient.patch(`/chat/conversations/${id}`, { title }),
  // POST /chat/conversations/:id/participants — add members (owner only)
  addParticipants: (id, userIds) =>
    httpClient.post(`/chat/conversations/${id}/participants`, { user_ids: userIds }),
  // DELETE /chat/conversations/:id/participants/:userId — remove a member (owner only)
  removeParticipant: (id, userId) =>
    httpClient.delete(`/chat/conversations/${id}/participants/${userId}`),
  // POST /chat/conversations/:id/leave — leave a conversation
  leave: (id) => httpClient.post(`/chat/conversations/${id}/leave`),
  // DELETE /chat/conversations/:id — delete the conversation (group: owner only)
  remove: (id) => httpClient.delete(`/chat/conversations/${id}`),
  // PATCH /chat/conversations/:id/messages/:mid — edit your message
  editMessage: (id, messageId, body) =>
    httpClient.patch(`/chat/conversations/${id}/messages/${messageId}`, { body }),
  // DELETE /chat/conversations/:id/messages/:mid — soft-delete your message
  deleteMessage: (id, messageId) =>
    httpClient.delete(`/chat/conversations/${id}/messages/${messageId}`),
  // GET /chat/conversations/:id/messages?limit=&before=
  listMessages: (conversationId, params) =>
    httpClient.get(`/chat/conversations/${conversationId}/messages`, { params }),
  // POST /chat/conversations/:id/messages — REST fallback for sending (with @mentions)
  send: (conversationId, body, mentions = []) =>
    httpClient.post(`/chat/conversations/${conversationId}/messages`, { body, mentions }),
  // POST /chat/conversations/:id/attachments — send a file (multipart)
  sendAttachment: (conversationId, file, caption = '') => {
    const form = new FormData();
    form.append('file', file);
    if (caption) form.append('caption', caption);
    return httpClient.post(`/chat/conversations/${conversationId}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // GET attachment as a blob (authenticated) — for preview/download
  downloadAttachment: (conversationId, messageId, attachmentId) =>
    httpClient.get(
      `/chat/conversations/${conversationId}/messages/${messageId}/attachments/${attachmentId}`,
      { responseType: 'blob' },
    ),
  // POST /chat/conversations/:id/mute — mute/unmute for the caller
  mute: (conversationId, muted) =>
    httpClient.post(`/chat/conversations/${conversationId}/mute`, { muted }),
  // POST /chat/conversations/:id/read
  markRead: (conversationId) =>
    httpClient.post(`/chat/conversations/${conversationId}/read`),
};

export default chatService;
